import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Heart, CheckCircle2, AlertTriangle, Loader2, Apple,
  Moon, Smile, Activity, Save,
} from 'lucide-react';
import { api } from './api';

/**
 * Daily Check-in — 3-step interactive flow.
 *
 *   Step 1: Inputs (appetite + sleep + mood + symptoms)
 *   Step 2: Submit & process (server-side trend + age-aware insight)
 *   Step 3: Result (status, observations, insight, action CTAs, focus)
 */

const APPETITE = [
  { id: 'good',    label: 'Good',    icon: Apple },
  { id: 'average', label: 'Average', icon: Apple },
  { id: 'poor',    label: 'Poor',    icon: Apple },
];
const SLEEP = [
  { id: 'good',      label: 'Good',      icon: Moon },
  { id: 'disturbed', label: 'Disturbed', icon: Moon },
  { id: 'poor',      label: 'Poor',      icon: Moon },
];
const MOOD = [
  { id: 'calm',      label: 'Calm',      icon: Smile },
  { id: 'irritable', label: 'Irritable', icon: Smile },
  { id: 'unusual',   label: 'Unusual',   icon: Smile },
];
const SYMPTOMS = [
  { id: 'none',  label: 'None' },
  { id: 'fever', label: 'Fever' },
  { id: 'cough', label: 'Cough' },
  { id: 'other', label: 'Other' },
];

const STATUS_PAL = {
  Stable:           { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  Monitor:          { text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
  'Needs attention':{ text: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200' },
};

const DailyCheckin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('inputs'); // inputs | result
  const [appetite, setAppetite] = useState('');
  const [sleep, setSleep] = useState('');
  const [mood, setMood] = useState('');
  const [symptoms, setSymptoms] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const canSubmit = appetite && sleep && mood;

  const toggleSymptom = (id) => {
    if (id === 'none') {
      setSymptoms(symptoms.includes('none') ? [] : ['none']);
      return;
    }
    const cleaned = symptoms.filter((x) => x !== 'none');
    setSymptoms(cleaned.includes(id) ? cleaned.filter((x) => x !== id) : [...cleaned, id]);
  };

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await api.postCheckin({ appetite, sleep, mood, symptoms });
      setResult(res);
      setStep('result');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save check-in.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = useMemo(() => (step === 'result' ? 100 : 50), [step]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/40 via-white to-white pb-24" data-testid="child-checkin">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate('/childrens-health')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4"
          data-testid="checkin-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
              <Heart className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Daily check-in</h1>
              <p className="text-xs text-gray-500 mt-0.5">A 60-second pulse — appetite, sleep, mood, symptoms.</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                {step === 'result' ? 'Result' : 'Step 1 of 2'}
              </span>
              <span className="text-[11px] font-semibold text-gray-400 tracking-wide">
                {step === 'inputs' ? 'How is today?' : "Today's snapshot"}
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-rose-500 to-pink-500 transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </header>

        <div key={step} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
        {step === 'inputs' && (
          <section className="space-y-4">
            <Group title="Appetite" testid="checkin-appetite" options={APPETITE} value={appetite} onChange={setAppetite} groupKey="appetite" />
            <Group title="Sleep" testid="checkin-sleep" options={SLEEP} value={sleep} onChange={setSleep} groupKey="sleep" />
            <Group title="Mood / Behaviour" testid="checkin-mood" options={MOOD} value={mood} onChange={setMood} groupKey="mood" />
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <h3 className="text-base font-bold text-gray-900 mb-1">Symptoms</h3>
              <p className="text-sm text-gray-600 mb-3">Select what applies.</p>
              <div className="grid grid-cols-2 gap-2">
                {SYMPTOMS.map((s) => {
                  const active = symptoms.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleSymptom(s.id)}
                      className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                        active ? 'border-rose-500 bg-rose-50 text-rose-900' : 'border-gray-200 hover:border-rose-300 text-gray-700'
                      }`}
                      data-testid={`checkin-symptom-${s.id}`}
                    >
                      {active && <CheckCircle2 className="h-4 w-4 inline mr-1.5 text-rose-600" />}
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {error && <p className="text-sm text-rose-700">{error}</p>}

            <div className="flex justify-end">
              <button
                onClick={submit}
                disabled={!canSubmit || submitting}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm transition-all ${
                  canSubmit && !submitting ? 'bg-gradient-to-br from-rose-500 to-pink-500 hover:brightness-110 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="checkin-submit"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? 'Saving…' : 'See snapshot'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}

        {step === 'result' && result && (
          <section data-testid="checkin-result" className="space-y-4">
            <div className={`rounded-2xl border-2 ${STATUS_PAL[result.status]?.border || 'border-gray-200'} ${STATUS_PAL[result.status]?.bg || 'bg-gray-50'} p-5`}>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Status</p>
              <h2 className="text-2xl font-bold text-gray-900">
                <span className={STATUS_PAL[result.status]?.text || 'text-gray-700'} data-testid="checkin-status">{result.status}</span>
                <span className="text-base text-gray-500 font-medium"> · {result.score}/100</span>
              </h2>
              <p className="text-xs text-gray-600 mt-1">Trend: {result.trend} · Age band: {result.age_band}</p>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <p className="text-[11px] font-semibold text-rose-700 uppercase tracking-widest mb-2">Insight</p>
              <p className="text-sm text-gray-800 leading-relaxed font-medium" data-testid="checkin-insight">{result.insight}</p>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">Key observations</p>
              <ul className="space-y-2" data-testid="checkin-observations">
                {result.observations.map((o) => (
                  <li key={o} className="text-sm text-gray-700 flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </div>

            {result.focus && result.focus.length > 0 && (
              <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="checkin-focus">
                <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-widest mb-2">Today's focus</p>
                <ul className="space-y-2">
                  {result.focus.map((f, i) => (
                    <li key={f} className="text-sm text-gray-800 flex items-start gap-2">
                      <span className="text-amber-600 font-bold">{i + 1}.</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-center gap-1.5 text-xs text-emerald-700" data-testid="checkin-saved">
              <Save className="h-3 w-3" /> Saved · check back tomorrow for trend
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {result.actions?.map((a) => (
                <button
                  key={a.label}
                  onClick={() => navigate(a.route)}
                  className="bg-gradient-to-br from-rose-500 to-pink-500 hover:brightness-110 text-white font-semibold rounded-xl px-5 py-3 shadow-sm transition-all"
                  data-testid={`checkin-action-${a.label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {a.label} →
                </button>
              ))}
              <button
                onClick={() => navigate('/childrens-health')}
                className="bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-xl px-5 py-3 border border-gray-200 transition-all"
                data-testid="checkin-back-hub"
              >
                Back to hub
              </button>
            </div>
          </section>
        )}
        </div>

        <p className="text-[11px] text-gray-400 text-center mt-6">
          HealthLoop supports awareness — it does not diagnose or replace pediatric care.
        </p>
      </div>
    </div>
  );
};

const Group = ({ title, options, value, onChange, groupKey, testid }) => (
  <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid={testid}>
    <h3 className="text-base font-bold text-gray-900 mb-3">{title}</h3>
    <div className="grid grid-cols-3 gap-2">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`text-sm font-medium rounded-xl border-2 px-3 py-2.5 transition-all ${
            value === o.id ? 'border-rose-500 bg-rose-50 text-rose-900' : 'border-gray-200 hover:border-rose-300 text-gray-700'
          }`}
          data-testid={`checkin-${groupKey}-${o.id}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

export default DailyCheckin;
